/**
 * Firebase Client Configuration
 * Full Firebase Integration:
 * - Authentication (Google, Email/Password, Anonymous)
 * - Analytics (User behavior tracking)
 * - Remote Config (Feature flags, Kill Switch)
 * - Cloud Messaging (Push notifications)
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
    getAuth, 
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInAnonymously,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    linkWithPopup,
    linkWithCredential,
    EmailAuthProvider,
    signOut,
    browserLocalPersistence,
    setPersistence,
    onAuthStateChanged
} from 'firebase/auth';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getRemoteConfig, fetchAndActivate, getValue, getString, getBoolean } from 'firebase/remote-config';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID // For Analytics
};

// VAPID key for Web Push notifications
const VAPID_KEY = 'BMN2NVHRM1Ae6x_xXO_odjJlYgNGW5lI0ofcPoOayfg5U2p6qpkOCBc97V1R-Fth7rZNr_0a2HOcKWYdJnMO9a4';

// Log config for debugging (without sensitive keys)
console.log('🔧 Firebase config:', {
    apiKey: firebaseConfig.apiKey ? `${firebaseConfig.apiKey.substring(0, 10)}...` : 'MISSING',
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId
});

// Initialize Firebase (singleton pattern)
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);

// =====================
// ANALYTICS SETUP
// =====================
let analytics = null;
try {
    // Analytics only works in browser environment
    if (typeof window !== 'undefined') {
        analytics = getAnalytics(app);
        console.log('📊 Firebase Analytics initialized');
    }
} catch (err) {
    console.warn('📊 Firebase Analytics not available:', err.message);
}

// Analytics helper functions
export const trackEvent = (eventName, params = {}) => {
    if (analytics) {
        logEvent(analytics, eventName, params);
        console.log(`📊 Event tracked: ${eventName}`, params);
    }
};

export const setAnalyticsUserId = (userId) => {
    if (analytics && userId) {
        setUserId(analytics, userId);
        console.log(`📊 Analytics user ID set: ${userId}`);
    }
};

export const setAnalyticsUserProperties = (properties) => {
    if (analytics) {
        setUserProperties(analytics, properties);
        console.log('📊 User properties set:', properties);
    }
};

// Pre-defined event trackers for common actions
export const trackOracleChat = (oracleType) => trackEvent('oracle_chat_started', { oracle_type: oracleType });
export const trackOracleChatComplete = (oracleType, messageCount) => trackEvent('oracle_chat_completed', { oracle_type: oracleType, message_count: messageCount });
export const trackOnboardingStep = (stepName) => trackEvent('onboarding_step', { step: stepName });
export const trackOnboardingComplete = () => trackEvent('onboarding_complete');
export const trackFeatureUsed = (featureName) => trackEvent('feature_used', { feature: featureName });
export const trackError = (errorType, errorMessage) => trackEvent('app_error', { error_type: errorType, error_message: errorMessage });
export const trackSignUp = (method) => trackEvent('sign_up', { method });
export const trackLogin = (method) => trackEvent('login', { method });
export const trackMemorySaved = (oracleType) => trackEvent('memory_saved', { oracle_type: oracleType });
export const trackImageGenerated = (oracleType) => trackEvent('image_generated', { oracle_type: oracleType });

// =====================
// REMOTE CONFIG SETUP
// =====================
let remoteConfig = null;
try {
    if (typeof window !== 'undefined') {
        remoteConfig = getRemoteConfig(app);
        // Settings for development - fetch every 10 seconds
        // In production, increase to 3600 (1 hour) or more
        remoteConfig.settings.minimumFetchIntervalMillis = 
            process.env.NODE_ENV === 'development' ? 10000 : 3600000;
        
        // Default values (used before first fetch)
        remoteConfig.defaultConfig = {
            ai_features_enabled: true,
            kill_switch_active: false,
            maintenance_mode: false,
            max_daily_chats: 50,
            image_generation_enabled: true,
            premium_features_enabled: false,
            welcome_message: 'Welcome to Astral Belonging',
            app_version_min: '1.0.0'
        };
        
        console.log('🎛️ Firebase Remote Config initialized');
    }
} catch (err) {
    console.warn('🎛️ Firebase Remote Config not available:', err.message);
}

// Fetch and activate remote config
export const fetchRemoteConfig = async () => {
    if (!remoteConfig) return false;
    try {
        const activated = await fetchAndActivate(remoteConfig);
        console.log('🎛️ Remote Config fetched and activated:', activated);
        return activated;
    } catch (err) {
        console.error('🎛️ Remote Config fetch failed:', err);
        return false;
    }
};

// Remote config getters
export const getConfigValue = (key) => {
    if (!remoteConfig) return null;
    return getValue(remoteConfig, key);
};

export const getConfigString = (key) => {
    if (!remoteConfig) return '';
    return getString(remoteConfig, key);
};

export const getConfigBoolean = (key) => {
    if (!remoteConfig) return false;
    return getBoolean(remoteConfig, key);
};

// Convenience getters for common configs
export const isKillSwitchActive = () => getConfigBoolean('kill_switch_active');
export const isMaintenanceMode = () => getConfigBoolean('maintenance_mode');
export const areAIFeaturesEnabled = () => getConfigBoolean('ai_features_enabled');
export const isImageGenerationEnabled = () => getConfigBoolean('image_generation_enabled');
export const getMaxDailyChats = () => {
    const val = getConfigValue('max_daily_chats');
    return val ? val.asNumber() : 50;
};

// =====================
// CLOUD MESSAGING SETUP
// =====================
let messaging = null;

// Initialize messaging only in supported browsers
const initializeMessaging = () => {
    try {
        if (typeof window === 'undefined') return null;
        if (!('serviceWorker' in navigator)) {
            console.warn('📬 Service Worker not supported in this browser');
            return null;
        }
        if (!('Notification' in window)) {
            console.warn('📬 Notifications not supported in this browser');
            return null;
        }
        // Check if running in a context that supports messaging
        if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
            console.warn('📬 FCM requires HTTPS or localhost');
            return null;
        }
        const msg = getMessaging(app);
        console.log('📬 Firebase Cloud Messaging initialized');
        return msg;
    } catch (err) {
        // Silently handle unsupported browsers (Safari, some mobile browsers)
        console.warn('📬 Firebase Cloud Messaging not available:', err.message);
        return null;
    }
};

// Lazy initialize messaging only when needed
const getMessagingInstance = () => {
    if (messaging === null) {
        messaging = initializeMessaging();
    }
    return messaging;
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
    const msg = getMessagingInstance();
    if (!msg) {
        console.warn('📬 Messaging not available');
        return null;
    }
    
    try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
            console.log('📬 Notification permission granted');
            const token = await getToken(msg, { vapidKey: VAPID_KEY });
            console.log('📬 FCM Token:', token?.substring(0, 20) + '...');
            return token;
        } else {
            console.log('📬 Notification permission denied');
            return null;
        }
    } catch (err) {
        console.error('📬 Error getting notification permission:', err);
        return null;
    }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
    const msg = getMessagingInstance();
    if (!msg) return () => {};
    
    return onMessage(msg, (payload) => {
        console.log('📬 Foreground message received:', payload);
        callback(payload);
    });
};

// =====================
// AUTH SETUP
// =====================

// CRITICAL: Use LOCAL persistence for redirect auth
setPersistence(auth, browserLocalPersistence)
    .then(() => console.log('🔐 Firebase: Persistence set to LOCAL'))
    .catch((err) => console.error('🔐 Firebase: Failed to set persistence:', err));

// Disable app verification for testing on preview domains
auth.settings.appVerificationDisabledForTesting = true;
console.log('🔐 Firebase: App verification disabled for testing');

// Configure Google provider with custom parameters
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({
    prompt: 'select_account'
});

// Auth state observer
export const onAuthChange = (callback) => {
    return onAuthStateChanged(auth, callback);
};

// Get current Firebase user
export const getCurrentUser = () => auth.currentUser;

// Get Firebase ID token for backend authentication
export const getIdToken = async (forceRefresh = false) => {
    const user = auth.currentUser;
    if (!user) return null;
    try {
        return await user.getIdToken(forceRefresh);
    } catch (err) {
        console.error('Failed to get ID token:', err);
        return null;
    }
};

// =====================
// EXPORTS
// =====================

export {
    app,
    auth,
    analytics,
    remoteConfig,
    messaging,
    googleProvider,
    GoogleAuthProvider,
    EmailAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInAnonymously,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    linkWithPopup,
    linkWithCredential,
    signOut,
    VAPID_KEY
};
